import { ReactNode, createContext, useEffect, useMemo, useState } from "react";

import { handleMatchPath } from "./utils";
import {
  MenuChildrenType,
  MenuItemNameType,
  MenuType,
  TabsMenuValuesType,
  TabType,
} from "contexts/TabsMenuContext/types";
import { PRIVATE_ROUTES } from "configs/routes";
import { useAuth } from "contexts/auth";

const tabs: TabType[] = [];

function filterMenuByUserRoles(routes: MenuType[], userRoleNames: string[]): MenuType[] {
  return routes
    .map((group) => ({
      ...group,
      children: group.children.filter(
        (child) =>
          !child.permissions?.length ||
          child.permissions.some((p) => userRoleNames.includes(p))
      ),
    }))
    .filter((group) => group.children.length > 0);
}

const initialData: TabsMenuValuesType = {
  menu: PRIVATE_ROUTES,
  flatMenu: [],
  tabs,
  onTabClick: () => {},
  onCloseTab: () => {},
  onMenuGroupClick: () => {},
  onMenuItemClick: () => {},
  updateTabPath: () => {},
  updateTabDescription: () => {},
  addNewTabOutsideOfMenu: () => {},
  getActiveTabItem: () => {
    return {
      title: "",
      description: "",
      parentName: "",
      id: "",
      isActive: false,
    };
  },
  isTabInActiveTabItems: () => false,
  activateTab: () => {},
  clearContext: () => {},
  addPinnedMenu: () => {},
  removePinnedMenu: () => {},
};

type TProps = {
  children: ReactNode;
};

export const TabsMenuContext = createContext(initialData);

function TabsMenuProvider({ children }: TProps) {
  const { user } = useAuth();
  const userRoleNames = user?.roles?.map((r) => r.name) ?? [];
  const filteredRoutes = filterMenuByUserRoles(PRIVATE_ROUTES, userRoleNames);

  const [menu, setMenu] = useState(filteredRoutes);
  const [tabs, setTabs] = useState(initialData.tabs);

  const flatMenu = useMemo((): MenuChildrenType[] => {
    const _flatMenuItems: MenuChildrenType[] = [];
    filteredRoutes.forEach((parent) => {
      parent.children.forEach((child) => {
        if (child.showInMenu) {
          _flatMenuItems.push(child);
        }
      });
    });
    return _flatMenuItems;
  }, [userRoleNames.join(",")]);

  //   const router = useRouter();

  useEffect(() => {
    addInitialTabBaseOnRefreshedUrl();
    addInitialTabOutsideMenuOnRefreshUrl();
    initPinnedMenusStatus();
  }, []);

  const initPinnedMenusStatus = () => {
    const storedPinnedMenus = JSON.parse(
      localStorage.getItem("pinnedMenus") || "[]"
    );

    const newMenu = menu.map((menuItem) => {
      const newChildren = menuItem.children.map((childrenItem) => {
        const isPinned = storedPinnedMenus.includes(childrenItem.name);
        return {
          ...childrenItem,
          isPinned,
        };
      });
      return {
        ...menuItem,
        children: newChildren,
      };
    });
    setMenu(newMenu);
  };
  const addPinnedMenu = (childrenItemName: MenuItemNameType) => {
    let pinnedMenus = [];
    const storedPinnedMenus = JSON.parse(
      localStorage.getItem("pinnedMenus") || "[]"
    );
    pinnedMenus = [...storedPinnedMenus, childrenItemName];
    localStorage.setItem("pinnedMenus", JSON.stringify(pinnedMenus));
    initPinnedMenusStatus();
  };
  const removePinnedMenu = (childrenItemName: MenuItemNameType) => {
    let pinnedMenus = [];
    const storedPinnedMenus = JSON.parse(
      localStorage.getItem("pinnedMenus") || "[]"
    );
    pinnedMenus = [...storedPinnedMenus];
    if (pinnedMenus.length > 0) {
      pinnedMenus.splice(pinnedMenus.indexOf(childrenItemName), 1);
    }
    localStorage.setItem("pinnedMenus", JSON.stringify(pinnedMenus));
    initPinnedMenusStatus();
  };
  const addInitialTabBaseOnRefreshedUrl = () => {
    // const currentUrl = router.asPath;
    const currentUrl = "";
    for (const menuItem of menu) {
      for (const menuChildrenItem of menuItem.children) {
        if (
          handleMatchPath({
            pattern: currentUrl,
            pathname: menuChildrenItem.path,
          })
        ) {
          const title =
            new URLSearchParams(window.location.search).get("tabName") ||
            menuChildrenItem.title;
          addNewTab({ ...menuChildrenItem, title });
        }
      }
    }
  };

  const addInitialTabOutsideMenuOnRefreshUrl = () => {
    // const currentUrl = router.asPath;
    // if (
    //   handleMatchPath({
    //     pattern: currentUrl,
    //     pathname: outsideMenuRoutes.userProfile.path,
    //   })
    // ) {
    //   addNewTab(outsideMenuRoutes.userProfile);
    // }
  };

  const calculateSimilarTabsForSpecificMenuChildCount = (
    menuChildrenItem: MenuChildrenType
  ) => {
    let similarTabsCount = 0;
    for (const tab of tabs) {
      tab.parentName === menuChildrenItem.name && similarTabsCount++;
    }
    return String(similarTabsCount);
  };
  const generateTabId = (menuChildrenItem: MenuChildrenType): string => {
    return `${
      menuChildrenItem.name
    }_${calculateSimilarTabsForSpecificMenuChildCount(menuChildrenItem)}`;
  };
  const changeTabsAndMenuActiveStatusBaseOnSelectedItem = (
    selectedItem: TabType
  ) => {
    const newMenu = menu.map((menuItem) => {
      const newChildren = menuItem.children.map((childrenItem) => {
        const isActive = selectedItem.parentName === childrenItem.name;
        return { ...childrenItem, isActive: isActive };
      });

      const isActive = newChildren.some(
        (childrenItem) => childrenItem.isActive
      );
      return { ...menuItem, children: newChildren, isActive: isActive };
    });
    setMenu(newMenu);

    const newTabs = tabs.map((tabItem) => {
      const isActive = selectedItem.id === tabItem.id;
      return {
        ...tabItem,
        isActive,
      };
    });
    setTabs(newTabs);
  };
  const changeMenuItemOpenStatus = (menuGroupItem: MenuType) => {
    const newMenu = menu.map((menuItem) =>
      menuItem.name !== menuGroupItem.name
        ? // if you're looking for to make the sidebar open all items just return menuItem in this line
          { ...menuItem, isOpen: false }
        : { ...menuGroupItem, isOpen: !menuGroupItem.isOpen }
    );
    setMenu(newMenu);
  };
  const onMenuGroupClick = (menuGroupItem: any) => {
    console.log("menuGroupItem", menuGroupItem);
    changeMenuItemOpenStatus(menuGroupItem);
  };
  const onMenuItemClick = (menuChildrenItem: MenuChildrenType) => {
    addNewTab(menuChildrenItem);
  };
  const onTabClick = (selectedTab: TabType) => {
    changeTabsAndMenuActiveStatusBaseOnSelectedItem(selectedTab);
  };
  const addNewTabOutsideOfMenu = (
    title: string,
    path: string,
    query?: any,
    description?: string
  ) => {
    let similarTabsCount = 0;
    for (const tab of tabs) {
      tab.parentName === "menuOutsider" && similarTabsCount++;
    }
    let pathWithQuery = path;
    if (query) {
      pathWithQuery = path + "?";
      for (const key in query) {
        pathWithQuery = pathWithQuery + `${key}=${query[key]}&`;
      }
    }
    const newTab: TabType = {
      title: title,
      description: description || "",
      id: `menuOutsider_${similarTabsCount}`,
      isActive: true,
      path: pathWithQuery,
      parentName: "menuOutsider",
    };
    changeTabsAndMenuActiveStatusBaseOnSelectedItem(newTab);
    const newTabs = tabs.map((tabItem) => {
      return {
        ...tabItem,
        isActive: false,
      };
    });
    newTabs.push(newTab);
    setTabs(newTabs);
  };
  const addNewTab = (
    menuChildrenItem: MenuChildrenType,
    description?: string,
    path?: string
  ) => {
    const tabId = generateTabId(menuChildrenItem);
    const newTab: TabType = {
      title: `${menuChildrenItem.title}`,
      description: description || "",
      id: tabId,
      isActive: true,
      path: path || menuChildrenItem.path,
      parentName: menuChildrenItem.name,
    };
    changeTabsAndMenuActiveStatusBaseOnSelectedItem(newTab);

    const newTabs = tabs.map((tabItem) => {
      return {
        ...tabItem,
        isActive: false,
      };
    });
    newTabs.push(newTab);
    setTabs(newTabs);
  };
  const updateTabPath = (tabItem: TabType, path: string) => {
    const newTabs = tabs.map((item) => {
      if (item.id === tabItem.id) {
        return {
          ...tabItem,
          path,
        };
      } else {
        return {
          ...item,
        };
      }
    });
    setTabs(newTabs);
  };
  const updateTabDescription = (tabItem: TabType, description: string) => {
    const newTabs = tabs.map((item) => {
      if (item.id === tabItem.id) {
        return {
          ...tabItem,
          description,
        };
      } else {
        return {
          ...item,
        };
      }
    });
    setTabs(newTabs);
  };
  const onCloseTab = (tabItem: TabType) => {
    const newTabs = tabs.map((tab) => tab);
    const closedTabIndex = newTabs.findIndex((item) => item.id === tabItem.id);
    closedTabIndex !== -1 && newTabs.splice(closedTabIndex, 1);
    let newActiveTab: TabType | undefined = undefined;
    if (newTabs && newTabs.length > 0 && tabItem.isActive) {
      // if you want select first tab after close any active tab uncomment code below
      // newTabs[0].isActive = true;
      // newActiveTab = newTabs[0];
      if (closedTabIndex === 0) {
        newTabs[closedTabIndex].isActive = true;
        newActiveTab = newTabs[closedTabIndex];
      } else {
        newTabs[closedTabIndex - 1].isActive = true;
        newActiveTab = newTabs[closedTabIndex - 1];
      }
    }
    setTabs(newTabs);

    if (tabItem.isActive) {
      const newMenu = menu.map((menuItem) => {
        const newChildren = menuItem.children.map((childrenItem) => {
          const isActive = childrenItem.name === newActiveTab?.parentName;
          return {
            ...childrenItem,
            isActive,
          };
        });
        return {
          ...menuItem,
          children: newChildren,
        };
      });
      setMenu(newMenu);
      //   newActiveTab ? router.push(`${newActiveTab.path}`) : router.push("/");
    }
  };
  const getActiveTabItem = () => {
    let activeTab;
    for (const item of tabs) {
      if (item.isActive) {
        activeTab = item;
      }
    }
    return activeTab;
  };

  const isTabInActiveTabItems = (item: MenuChildrenType) => {
    for (const tab of tabs) {
      if (tab.path?.split("?")[0] === item.path) {
        return true;
      }
    }
    return false;
  };

  const activateTab = (item: MenuChildrenType) => {
    const targetTab = tabs.find(
      (t: TabType) => t.path?.split("?")[0] === item.path
    );
    if (targetTab) {
      onTabClick(targetTab);
    }
  };

  const clearContext = () => {
    const newMenu = menu.map((menuItem) => {
      const newMenuItemChildren = menuItem.children.map((menuItemChild) => {
        return { ...menuItemChild, isOpen: false, isActive: false };
      });
      return {
        ...menuItem,
        children: newMenuItemChildren,
        isOpen: false,
        isActive: false,
      };
    });
    setMenu(newMenu);
    setTabs([]);
  };

  const values = {
    menu,
    flatMenu: flatMenu,
    tabs,
    onTabClick,
    onCloseTab,
    onMenuGroupClick,
    onMenuItemClick,
    addNewTabOutsideOfMenu,
    updateTabPath,
    updateTabDescription,
    getActiveTabItem,
    isTabInActiveTabItems,
    activateTab,
    clearContext,
    addPinnedMenu,
    removePinnedMenu,
  };
  return (
    <TabsMenuContext.Provider value={values}>
      {children}
    </TabsMenuContext.Provider>
  );
}

export default TabsMenuProvider;
